import { useEffect, useState } from "react"
import { router } from "@inertiajs/react"
import {
  ResponsiveDialog,
  ResponsiveDialogContent,
  ResponsiveDialogHeader,
  ResponsiveDialogTitle,
  ResponsiveDialogDescription,
  ResponsiveDialogFooter,
} from "@/components/ui/responsive-dialog"
import { Button } from "@/components/ui/button"
import { CurrencyUsdInput } from "@/components/ui/currency-usd-input"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { moneyPropToString, normalizeMoneyCanonical } from "@/lib/money-input"
import {
  DynamicList,
  TextFieldRow,
} from "@/pages/App/Plan/Intake/components/DynamicList"

// Inline one-year-plan editor. Members can update their revenue/profit
// targets, measurables, and the issues list without walking through the
// intake wizard. Submits a single PATCH to AnnualPlansController#update.
//
// Props:
//   open, onOpenChange  controlled
//   plan                { id, revenueTarget, profitTarget, measurables, targetDate, issues, lockVersion }
export function OneYearPlanEditDialog({ open, onOpenChange, plan }) {
  const [targetDate, setTargetDate] = useState(plan?.targetDate || "")
  const [revenueTarget, setRevenueTarget] = useState(() => moneyPropToString(plan?.revenueTarget))
  const [profitTarget, setProfitTarget] = useState(() => moneyPropToString(plan?.profitTarget))
  const [measurables, setMeasurables] = useState(plan?.measurables || "")
  const [issues, setIssues] = useState(() => seedIssues(plan?.issues))
  const [processing, setProcessing] = useState(false)
  const [error, setError] = useState(null)

  // Re-seed when the dialog re-opens or the plan prop changes (after
  // a successful save reloaded the page props).
  useEffect(() => {
    if (open) {
      setTargetDate(plan?.targetDate || "")
      setRevenueTarget(moneyPropToString(plan?.revenueTarget))
      setProfitTarget(moneyPropToString(plan?.profitTarget))
      setMeasurables(plan?.measurables || "")
      setIssues(seedIssues(plan?.issues))
      setError(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, plan?.lockVersion])

  const submit = (e) => {
    e?.preventDefault?.()
    if (processing || !plan?.id) return
    setProcessing(true)
    setError(null)

    fetch(`/app/planning/annual_plans/${plan.id}`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token":
          document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") || "",
        Accept: "application/json",
      },
      body: JSON.stringify({
        target_date: targetDate || null,
        revenue_target: normalizeMoneyCanonical(revenueTarget) || null,
        profit_target: normalizeMoneyCanonical(profitTarget) || null,
        measurables: measurables || null,
        issues: issues.filter((i) => (i.text || "").trim()),
        lock_version: plan.lockVersion,
      }),
      credentials: "same-origin",
    })
      .then(async (res) => {
        if (res.ok) {
          onOpenChange(false)
          router.reload({ only: ["annualPlan"] })
        } else {
          const json = await res.json().catch(() => ({}))
          setError(json.error || "Could not save. Please try again.")
        }
      })
      .catch(() => setError("Network error. Please try again."))
      .finally(() => setProcessing(false))
  }

  return (
    <ResponsiveDialog open={open} onOpenChange={onOpenChange}>
      <ResponsiveDialogContent className="sm:max-w-2xl">
        <form onSubmit={submit}>
          <ResponsiveDialogHeader>
            <ResponsiveDialogTitle className="font-display text-xl">
              Edit one-year plan
            </ResponsiveDialogTitle>
            <ResponsiveDialogDescription>
              Update the targets and issues you're steering toward this year.
            </ResponsiveDialogDescription>
          </ResponsiveDialogHeader>

          <div className="px-4 sm:px-6 py-4 space-y-4">
            {error && (
              <div
                role="alert"
                className="flex items-center justify-between gap-3 rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-800"
              >
                <span>{error}</span>
                <button
                  type="button"
                  onClick={() => setError(null)}
                  className="text-red-700 hover:text-red-900"
                >
                  Dismiss
                </button>
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="oyp-target-date">Target date</Label>
                <Input
                  id="oyp-target-date"
                  type="date"
                  value={targetDate}
                  onChange={(e) => setTargetDate(e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="oyp-revenue">Revenue target ($)</Label>
                <CurrencyUsdInput
                  id="oyp-revenue"
                  value={revenueTarget}
                  onValueChange={setRevenueTarget}
                  placeholder="$500,000"
                />
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="oyp-profit">Profit target ($)</Label>
                <CurrencyUsdInput
                  id="oyp-profit"
                  value={profitTarget}
                  onValueChange={setProfitTarget}
                  placeholder="$100,000"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="oyp-measurables">Measurables</Label>
              <Textarea
                id="oyp-measurables"
                rows={3}
                value={measurables}
                onChange={(e) => setMeasurables(e.target.value)}
                placeholder="Other metrics that tell you you're on track…"
              />
            </div>

            <div>
              <Label>Top 3–7 issues to solve or address</Label>
              <p className="text-xs text-zinc-500 mb-2">
                The real blockers — what's in the way?
              </p>
              <DynamicList
                items={issues}
                onChange={setIssues}
                min={1}
                max={7}
                newItem={() => ({ text: "" })}
                addLabel="Add another issue"
                render={(item, update) => (
                  <TextFieldRow
                    value={item.text}
                    onChange={(v) => update("text", v)}
                    placeholder="e.g., Hiring an ops lead so I'm not in every detail"
                  />
                )}
              />
            </div>
          </div>

          <ResponsiveDialogFooter>
            <Button
              type="button"
              variant="kf-outline"
              onClick={() => onOpenChange(false)}
              disabled={processing}
            >
              Cancel
            </Button>
            <Button type="submit" variant="gradient" disabled={processing}>
              {processing ? "Saving…" : "Save"}
            </Button>
          </ResponsiveDialogFooter>
        </form>
      </ResponsiveDialogContent>
    </ResponsiveDialog>
  )
}

function seedIssues(items) {
  if (!items || items.length === 0) {
    return [{ text: "" }]
  }
  return items.map((it) => ({
    id: it.id,
    text: it.text || "",
  }))
}
